import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Container,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Paper,
  Divider,
  Chip,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Fab,
  Badge,
} from '@mui/material';
import {
  ShoppingCart,
  Add,
  CheckCircle,
  ArrowBack,
  Restaurant,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { tableService } from '../../services/tableService';
import { menuService } from '../../services/menuService';
import { orderService } from '../../services/orderService';
import { buildImageUrl } from '../../utils/imageUtils';
import { useNotification } from '../../contexts/NotificationContext';
import { useRestaurant } from '../../contexts/RestaurantContext';
import TableOrderLayout from '../../components/user/TableOrderLayout';
import CartDrawer from '../../components/user/CartDrawer';
import MenuItemModal from '../../components/user/MenuItemModal';

const TableOrderPage = () => {
  const { uniqueString } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tableData, setTableData] = useState(null);
  
  // Menu related state
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('counter');
  const [specialInstructions, setSpecialInstructions] = useState('');
  
  const { showNotification } = useNotification();
  const { 
    cart, 
    addToCart, 
    getCartItemCount, 
    getCartTotal,
    updateCartItem,
    removeFromCart,
    clearCart
  } = useRestaurant();

  useEffect(() => {
    const fetchData = async () => {
      try {
    setLoading(true);
        console.log('Fetching table data for uniqueString:', uniqueString);
        
        // Fetch table data
        const tableResponse = await tableService.getTableOrderPage(uniqueString);
        console.log('Table data response:', tableResponse);
        setTableData(tableResponse);
        
        // Fetch menu data
        const [categoriesResponse, menuResponse] = await Promise.all([
          menuService.getPublicCategories(),
          menuService.getPublicMenuItems(tableResponse.outlet.id)
        ]);
        
        setCategories(categoriesResponse);
        setMenuItems(menuResponse);
        
        if (categoriesResponse.length > 0) {
          setSelectedCategory(categoriesResponse[0].id);
        }
        
      } catch (err) {
        console.error('Error fetching data:', err);
        console.error('Error details:', err.response?.data);
        setError('Table not found or link is invalid');
    } finally {
      setLoading(false);
    }
  };

    if (uniqueString) {
      fetchData();
    }
  }, [uniqueString]);

  // Cart functions using RestaurantContext
  const handleAddToCart = (item) => {
    setSelectedItem(item);
  };

  const handlePlaceOrder = async () => {
    if (cart.length === 0) {
      showNotification('Your cart is empty', 'error');
      return;
    }

    if (!customerName.trim()) {
      showNotification('Please enter your name', 'error');
      return;
    }

    try {
      const orderData = {
        table_id: tableData.table.id,
        outlet_id: tableData.outlet.id,
        table_number: tableData.table.table_number,
        customer_name: customerName.trim(),
        customer_phone: customerPhone.trim() || null,
        customer_email: null,
        items: cart.map(item => ({
          menu_item_id: item.id,
          quantity: item.quantity,
          size_name: item.selectedSize?.name || null,
          size_price: item.selectedSize?.price || 0,
          base_price: item.base_price,
          total_price: item.price * item.quantity,
          special_instructions: item.special_instructions || null,
          addons: (item.addons || []).map(addon => ({
            addon_id: addon.id,
            quantity: addon.quantity,
            price: addon.price
          }))
        })),
        subtotal: getCartTotal(),
        tax_amount: 0,
        discount_amount: 0,
        total_amount: getCartTotal(),
        special_instructions: specialInstructions.trim() || null,
        payment_method: paymentMethod,
        status: paymentMethod === 'counter' ? 'pending' : 'pending'
      };

      console.log('Placing table order:', orderData);
      
      const order = await orderService.createTableOrder(orderData);
      console.log('Order created successfully:', order);
      
      if (paymentMethod === 'online') {
        // For online payments, create HitPay payment
        try {
          const payment = await orderService.createTableOrderPayment(order.id);
          console.log('Payment created:', payment);
          
          if (payment?.checkout_url) {
            // Store order info for after payment
            sessionStorage.setItem('pendingOrder', JSON.stringify(order));
            // Redirect to HitPay checkout
            window.location.href = payment.checkout_url;
          } else {
            showNotification('Failed to create payment. Please try again.', 'error');
          }
        } catch (paymentErr) {
          console.error('Payment creation error:', paymentErr);
          showNotification('Failed to create payment. Please try again.', 'error');
        }
      } else {
        // For counter payments, show success message
        showNotification('Order placed successfully! Your order number is ' + order.order_number, 'success');
        clearCart();
        setOrderDialogOpen(false);
        setCustomerName('');
        setCustomerPhone('');
        setSpecialInstructions('');
        setPaymentMethod('counter');
      }
      
    } catch (err) {
      console.error('Error placing order:', err);
      showNotification('Failed to place order. Please try again.', 'error');
    }
  };

  // Filter menu items by selected category
  const filteredMenuItems = selectedCategory 
    ? menuItems.filter(item => item.category_id === selectedCategory)
    : menuItems;

  if (loading) {
    return (
      <TableOrderLayout title="Loading..." subtitle="Please wait...">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress size={60} />
        </Box>
      </TableOrderLayout>
    );
  }

  if (error || !tableData) {
    return (
      <TableOrderLayout title="Table Not Found" subtitle="Invalid table link">
        <Container maxWidth="md">
          <Alert severity="error" sx={{ textAlign: 'center', py: 4 }}>
            {error || 'This table link is invalid or the table is no longer available.'}
          </Alert>
          <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Button
          variant="contained"
              startIcon={<ArrowBack />}
              onClick={() => navigate('/')}
            >
              Back to Home
          </Button>
      </Box>
        </Container>
      </TableOrderLayout>
  );
  }

  const { table, outlet } = tableData;

  return (
    <TableOrderLayout 
      title="Order Food" 
      showCart={true}
      cartItemCount={getCartItemCount()}
      tableID = {tableData.table.id}
    >
      <Container maxWidth="lg">

        {/* Menu Section */}
        <Box sx={{ mt: 6 }}>
          <Typography variant="h4" fontWeight="bold" textAlign="center" gutterBottom sx={{ mb: 4 }}>
            Our Menu
          </Typography>
          
          {/* Category Tabs */}
          {categories.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Tabs
                value={selectedCategory}
                onChange={(e, newValue) => setSelectedCategory(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                {categories.map((category) => (
                  <Tab
                    key={category.id}
                    label={category.name}
                    value={category.id}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  />
                ))}
              </Tabs>
            </Box>
          )}

          {/* Menu Items Grid */}
          <Grid container spacing={3}>
            {filteredMenuItems.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <Card sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}>
                  {item.image_url && (
                    <Box sx={{ 
                      height: 200, 
                      overflow: 'hidden',
                      position: 'relative'
                    }}>
                      <img
                        src={buildImageUrl(item.image_url)}
                        alt={item.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                      {item.discount_enabled && item.discount_percentage > 0 && (
                        <Chip
                          label={`${item.discount_percentage}% OFF`}
                          color="error"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            fontWeight: 'bold'
                          }}
                        />
                      )}
                    </Box>
                  )}
                  
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" fontWeight="600" gutterBottom>
                      {item.name}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
                      {item.description}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                      <Typography variant="h6" color="primary" fontWeight="bold">
                        S${item.discount_enabled && item.discount_percentage > 0 
                          ? (item.base_price * (1 - item.discount_percentage / 100)).toFixed(2)
                          : item.base_price}
                      </Typography>
                      
                      {item.discount_enabled && item.discount_percentage > 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                          S${item.base_price}
                        </Typography>
                      )}
                    </Box>
                    
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={() => handleAddToCart(item)}
                      sx={{ 
                        mt: 2,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
                        '& .MuiButton-startIcon': {
                          fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.9rem' }
                        }
                      }}
                    >
                      Add to Cart
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Floating Action Button for Cart */}
        {cart.length > 0 && (
          <Fab
            color="primary"
            aria-label="cart"
            onClick={() => setCartOpen(true)}
            sx={{
              position: 'fixed',
              bottom: isMobile ? 16 : 24,
              right: isMobile ? 16 : 24,
              zIndex: 1000,
            }}
          >
            <Badge badgeContent={getCartItemCount()} color="error">
              <ShoppingCart />
            </Badge>
          </Fab>
        )}

        {/* Cart Drawer */}
        <CartDrawer
          open={cartOpen}
          onClose={() => setCartOpen(false)}
          cart={cart}
          onUpdateQuantity={(itemId, quantity) => {
            if (quantity === 0) {
              removeFromCart(itemId);
            } else {
              updateCartItem(itemId, { quantity });
            }
          }}
          onRemoveItem={removeFromCart}
          onCheckout={() => setOrderDialogOpen(true)}
          checkoutButtonText="Place Order"
          checkoutButtonIcon={<CheckCircle />}
        />

        {/* Order Dialog */}
        <Dialog 
          open={orderDialogOpen} 
          onClose={() => setOrderDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6" fontWeight="600">
              Complete Your Order
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <TextField
                fullWidth
                label="Your Name *"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Phone Number (Optional)"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                margin="normal"
              />
              
              <TextField
                fullWidth
                label="Special Instructions (Optional)"
                value={specialInstructions}
                onChange={(e) => setSpecialInstructions(e.target.value)}
                margin="normal"
                multiline
                rows={3}
                placeholder="Any special requests for your order..."
              />
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" gutterBottom>
                Payment Method
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button
                  variant={paymentMethod === 'counter' ? 'contained' : 'outlined'}
                  onClick={() => setPaymentMethod('counter')}
                  startIcon={<Restaurant />}
                  sx={{ flex: 1 }}
                >
                  Pay at Counter
                </Button>
                <Button
                  variant={paymentMethod === 'online' ? 'contained' : 'outlined'}
                  onClick={() => setPaymentMethod('online')}
                  startIcon={<ShoppingCart />}
                  sx={{ flex: 1 }}
                >
                  Pay Online
                </Button>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" gutterBottom>
                Order Summary
              </Typography>
              {cart.map((item) => (
                <Box key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">
                    {item.name} x {item.quantity}
                  </Typography>
                  <Typography variant="body2">
                    S${item.price * item.quantity}
                  </Typography>
                </Box>
              ))}
              
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6">Total:</Typography>
                <Typography variant="h6" color="primary" fontWeight="bold">
                  S${getCartTotal()}
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOrderDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handlePlaceOrder}
              startIcon={<CheckCircle />}
            >
              Place Order
            </Button>
          </DialogActions>
        </Dialog>

        {/* Menu Item Modal */}
        <MenuItemModal
          open={!!selectedItem}
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onAddToCart={(item, quantity, addons, specialInstructions, selectedSize) => {
            addToCart(item, quantity, addons, specialInstructions, selectedSize);
            setSelectedItem(null);
          }}
        />
      </Container>
    </TableOrderLayout>
  );
};

export default TableOrderPage;